import React, { useState, useEffect, useMemo } from "react";
import firebase from "firebase";
import { Badge, Button, Form, Spinner } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import parse from "html-react-parser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faLink,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { Link, useHistory } from "react-router-dom";
import produce from "immer";
import { connect } from "react-redux";
import DatePicker from "react-date-picker";
import moment from "moment";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { randomBytes } from "crypto";
import {
  API_BASE_URL,
  DRIVE_UPLOAD,
  EVENT_PLAN_STATUS,
  SUPER_ADMIN_EMAIL,
} from "../../../config/index";
import { postData } from "../../../utils";

toast.configure();

const Addevent = (props) => {
  // CONSTANTS
  const currentLanguage = "EN";
  const history = useHistory();
  const currentUser = firebase.auth().currentUser;
  const { isCourse } = props;

  // STATES
  const [eventDetails, setEventDetails] = useState({
    admin_email: currentUser.email,
  });
  const [fileSizeError, setFileSizeError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startDateCalendar, setStartDateCalendar] = useState(false);
  const [endDateCalendar, setEndDateCalendar] = useState(false);
  const [checkingActiveEventPlan, setCheckingActiveEventPlan] = useState(true);
  const [activeEventPlan, setActiveEventPlan] = useState(null);
  const [availableQuantity, setAvailableQuantity] = useState(0);
  const [participantsQuantity, setParticipantsQuantity] = useState(0);
  const [roomDetails, setRoomDetails] = useState(null);

  // FUNCTIONS
  const checkPlan = async () => {
    // Checking events and courses plans
    try {
      console.log("[Try] Checking events and courses plans", {
        user_id: currentUser.uid,
      });

      const response = await axios.post(
        `${API_BASE_URL}${EVENT_PLAN_STATUS}`,
        {
          user_id: currentUser.uid,
        }
      );

      if (response.status === 200) {
        const { data, message } = response.data;
        console.log(
          "[Response] Checking events and courses plans: "
          // message
        );
        setActiveEventPlan(data);

        if (isCourse) {
          setAvailableQuantity(data?.planDetails?.courses?.available);
          setParticipantsQuantity(data?.planDetails?.courses?.participants);
        } else {
          setAvailableQuantity(data?.planDetails?.events?.available);
          setParticipantsQuantity(data?.planDetails?.events?.participants);
        }

        setCheckingActiveEventPlan(false);
      } else {
        setActiveEventPlan(null);
        setCheckingActiveEventPlan(false);
      }
    } catch (error) {
      console.log("[Error] Checking events and courses plans: ", error);
      setCheckingActiveEventPlan(false);
    }
  };

  const saveTextDataToStateHandler = (e, lang) => {
    setEventDetails((prev) => {
      const updatedState = produce(prev, (draft) => {
        draft[lang] = {
          ...(prev[lang] ?? ""),
          [e.target.name]: e.target.value,
        };
      });
      return updatedState;
    });
  };

  const saveHTMLDataToStateHandler = (name, data, lang) => {
    setEventDetails((prev) => {
      const updatedState = produce(prev, (draft) => {
        draft[lang] = {
          ...(prev[lang] ?? ""),
          [name]: data,
        };
      });
      return updatedState;
    });
  };

  const uploadFile = async (file) => {
    if (file.name) {
      const fileData = new FormData();
      fileData.append("file", file, `${file.lastModified}-${file.name}`);
      fileData.append(
        "user_id",
        currentUser?.uid ?? "1gHTSDwzWRPW7KAUeTcscDFxs7z2"
      );
      const config = {
        headers: {
          "content-type": "multipart/form-data",
        },
      };
      if (file) {
        try {
          let res = await axios.post(
            `${API_BASE_URL}${DRIVE_UPLOAD}`,
            fileData,
            config
          );
          return res.data.data["file_url"];
        } catch (error) {
          console.error(error, "error in writing file to db");
        }
      } else return "";
    } else {
      return file;
    }
  };

  const messageEventCreated = () => (
    <div className="d-flex align-items-center">
      <FontAwesomeIcon
        icon={faCheckCircle}
        className="mr-2 my-0 text-success"
      />
      {isCourse ? (
        <p className="mb-0">Course successfully created</p>
      ) : (
        <p className="mb-0">Event successfully created</p>
      )}
    </div>
  );

  const addEventManager = async (docRefId, email) => {
    firebase
      .firestore()
      .collection("events")
      .doc(docRefId)
      .update({
        event_manager: firebase.firestore.FieldValue.arrayUnion(email)
      });
  }

  const saveEventDataToDBHandler = async (e, eventData) => {
    e.preventDefault();
    setSaving(true);

    const feature_image_url = eventData.featured_image
      ? await uploadFile(eventData.featured_image)
      : null;

    const event_data = {
      ...eventData,
      featured_image: feature_image_url,
    };

    if (availableQuantity === 0) {
      const message = () => (
        <div className="d-flex align-items-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="mr-2 my-0 text-warning"
          />
          <div>
            <p className="mb-0">You have exhausted your limit</p>
            <p className="mb-0 small">
              <a className="pointer" onClick={() => history.push("/pricing")}>
                Click here
              </a>{" "}
              to buy more events
            </p>
          </div>
        </div>
      );
      toast.dark(message, {
        position: toast.POSITION.TOP_CENTER,
        hideProgressBar: false,
        autoClose: 3000,
      });
      return;
    }

    const uid = randomBytes(4).toString("hex");

    try {
      // Creating and linking room in case of Course
      if (isCourse) {
        const payload = {
          workspace_id: "LgT2sjzoZQwwKMGPnob2",
          user_id: currentUser?.uid,
          room_name: event_data?.EN?.event_name,
          room_type: "0",
          video_server_location: "ap-south-1.igesia.com",
          room_pic:
            "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",
          room_pic_name: "default-room.png",
          room_pic_mime: "image/svg+xml",
          room_description: event_data?.EN?.small_description,
          for_event: true,
        };
        // Creating room for event
        const response = await postData(
          `${API_BASE_URL}/rooms/create`,
          payload
        );
        if (response.status === 200) {
          console.log(
            "[Success] Creating room for course"
            // response
          );
        } else {
          console.log("[Error] Creating room for course", response);
        }

        const payload_invite = {
          workspace_id: response?.data?.workspace_id,
          room_id: response?.data?.room_id,
          user_id: currentUser?.uid,
          expiration_days: 365,
        };
        // Generating room invite link
        const response_invite = await postData(
          `${API_BASE_URL}/invites/link/generate`,
          payload_invite
        );
        if (response_invite.status === 200) {
          console.log(
            "[Success] Generating room invite for course"
            // response_invite
          );
        } else {
          console.log(
            "[Error] Generating room invite for course",
            response_invite
          );
        }

        const docRef = firebase.firestore().collection(`/events/`).doc();
        await docRef.set(
          {
            event_details: {
              ...event_data,
            },
            isDemo: true,
            slug: `${event_data.EN.event_name
              ?.trim()
              .replace(/ /g, "-")
              .toLowerCase()}-${uid}`,
            timestamp: Date.now(),
            agenda_available: false,
            booths_available: false,
            created_by: currentUser?.uid,
            participants:
              activeEventPlan?.planType === 1 ? participantsQuantity : 10,
            type: 1,
            workspace_id: response?.data?.workspace_id,
            room_id: response?.data?.room_id,
            room_invite_code: response_invite?.data?.invite_link?.replace(
              "https://web.igesia.co/#/",
              ""
            ),
          },
          { merge: true }
        );

        toast.dark(messageEventCreated, {
          position: toast.POSITION.TOP_CENTER,
          hideProgressBar: false,
          autoClose: 3000,
        });

        //add Super Admin and Admin to the Event Manager - Course

        try {
          
          await addEventManager(docRef.id, SUPER_ADMIN_EMAIL);
          await addEventManager(docRef.id, currentUser.email);

        } catch (error) {
          console.log("[ Error ] - Failed to add Super Admin or Admin to EM (Course)")
        }
        
        history.push(`/admin/eventmanager/${docRef.id}/overview`);
      } else {
        const docRef = firebase.firestore().collection(`/events/`).doc();
        await docRef.set(
          {
            event_details: {
              ...event_data,
            },
            isDemo: true,
            slug: `${event_data.EN.event_name
              ?.trim()
              .replace(/ /g, "-")
              .toLowerCase()}-${uid}`,
            timestamp: Date.now(),
            agenda_available: false,
            booths_available: false,
            created_by: currentUser?.uid,
            participants:
              activeEventPlan?.planType === 1 ? participantsQuantity : 10,
            type: 0,
          },
          { merge: true }
        );

        toast.dark(messageEventCreated, {
          position: toast.POSITION.TOP_CENTER,
          hideProgressBar: false,
          autoClose: 3000,
        });

        //add Super Admin and Admin to the Event Manager - Event

        try {

          await addEventManager(docRef.id, SUPER_ADMIN_EMAIL);
          await addEventManager(docRef.id, currentUser.email);

        } catch (error) {
          console.log("[ Error ] - Failed to add Super Admin or Admin to EM (Event)")
        }

        history.push(`/admin/eventmanager/${docRef.id}/overview`);
      }
    } catch (e) {
      console.error(e, "error in saving to db");
      toast.error(
        isCourse ? "Error in creating course" : "Error in creating event",
        {
          position: toast.POSITION.TOP_CENTER,
          hideProgressBar: true,
          autoClose: 3000,
        }
      );
    } finally {
      setSaving(false);
    }
  };

  const getWhetherURLOrLocalFile = (file) => {
    if (!file.name) {
      return file;
    } else {
      return URL.createObjectURL(file);
    }
  };

  const featured_image_url = useMemo(() => {
    return eventDetails?.featured_image
      ? getWhetherURLOrLocalFile(eventDetails?.featured_image)
      : null;
  }, [eventDetails]);

  useEffect(() => {
    checkPlan();
  }, []);

  return (
    <div className="container-fluid">
      <div className="row text-white">
        <div className="col-12 border-bottom border-dark px-4 px-md-5 py-3 shadow-sm">
          <div className="d-flex justify-content-between ">
            <div>&nbsp;</div>
            <h5 className="my-0">
              <span className="mr-2">
                Add new&nbsp;
                {isCourse ? (
                  <span className="">Course</span>
                ) : (
                  <span className="">Event</span>
                )}
              </span>
              {checkingActiveEventPlan ? (
                <p className="mb-0">
                  <Spinner animation="spinner" size="sm" />{" "}
                  <span className="small">Checking plan...</span>
                </p>
              ) : activeEventPlan?.planType === 1 ? (
                participantsQuantity > 10 ? (
                  <Badge className="mr-2" variant="primary">
                    {participantsQuantity === 1000 ? "Pro" : "Basic"}
                  </Badge>
                ) : (
                  <Badge className="mr-2" variant="success">
                    Free
                  </Badge>
                )
              ) : (
                <Badge className="mr-2" variant="success">
                  Free
                </Badge>
              )}
            </h5>
            <Link to="/admin" className="text-decoration-none text-white my-0">
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </Link>
          </div>

          {!checkingActiveEventPlan && availableQuantity === 0 && (
            <p className="mb-0 mt-2">
              <Badge variant="secondary" className="">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-warning"
                />{" "}
                Limit exhausted
              </Badge>
              <Badge
                variant="primary"
                className="ml-2 pointer "
                onClick={() => history.push("/pricing")}
              >
                <FontAwesomeIcon icon={faLink} /> Click to buy more
              </Badge>
            </p>
          )}
        </div>
        <div
          className="col-md-6 px-4 px-md-5 offset-md-3"
          // style={{ maxHeight: "85vh", overflow: "auto" }}
        >
          <Form onSubmit={(e) => saveEventDataToDBHandler(e, eventDetails)}>
            <Form.Group>
              <Form.Label className="mt-2">
                Name
                {isCourse ? (
                  <p className="small text-secondary mb-0">Course name</p>
                ) : (
                  <p className="small text-secondary mb-0">Event name</p>
                )}
              </Form.Label>
              <Form.Control
                maxLength="200"
                type="text"
                className="mb-3"
                placeholder="Name"
                value={eventDetails?.EN?.event_name ?? ""}
                name="event_name"
                onChange={(event) => {
                  saveTextDataToStateHandler(event, "EN");
                }}
              />
            </Form.Group>
            {/*  <Form.Group className>
              <Form.Label>
                Nom de l'événement
                <p className="small text-secondary mb-0">
                  Nom de l'entreprise / nom de marque
                </p>
              </Form.Label>
              <Form.Control
                maxLength="200"
                type="text"
                className="mb-3"
                placeholder="Nom de l'événement"
                value={eventDetails?.FR?.event_name ?? ""}
                name="event_name"
                onChange={(event) => {
                  saveTextDataToStateHandler(event, "FR");
                }}
              />
            </Form.Group> */}
            <Form.Group>
              <Form.Label>
                Featured Image
                <p className="small text-secondary mb-0">
                  Recommdended: 1200px X 628px
                </p>
              </Form.Label>
              {eventDetails && eventDetails?.featured_image && (
                <img
                  src={featured_image_url}
                  className="w-100 mb-3 rounded-12"
                  alt="..."
                />
              )}
              <Form.Control
                type="file"
                className="mb-3"
                accept="image/*"
                placeholder="Featured Image"
                name="featured_image"
                onChange={(event) => {
                  setEventDetails((prevDetails) => {
                    return {
                      ...prevDetails,
                      featured_image: event.target.files[0],
                    };
                  });
                }}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                Small Description
                <p className="small text-secondary mb-0">
                  Recommdended: Min. 50 words (Max. 400 characters)
                </p>
              </Form.Label>
              <CKEditor
                id="editor"
                editor={ClassicEditor}
                config={{
                  toolbar: [
                    "heading",
                    "bold",
                    "italic",
                    "link",
                    "numberedList",
                    "bulletedList",
                    "undo",
                    "redo",
                  ],
                }}
                className="mb-3"
                data={eventDetails?.EN?.small_description ?? ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  saveHTMLDataToStateHandler("small_description", data, "EN");
                }}
                style={{ height: 400 }}
              />
            </Form.Group>
            {/*<Form.Group>
              <Form.Label>
                Petite description
                <p className="small text-secondary mb-0">
                  Recommandé: Min. 50 mots (max.400 caractères)
                </p>
              </Form.Label>
              <CKEditor
                id="editor"
                editor={ClassicEditor}
                config={{
                  toolbar: [
                    "heading",
                    "bold",
                    "italic",
                    "link",
                    "numberedList",
                    "bulletedList",
                    "undo",
                    "redo",
                  ],
                }}
                className="mb-3"
                data={eventDetails?.FR?.small_description ?? ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  saveHTMLDataToStateHandler("small_description", data, "FR");
                }}
                style={{ height: 400 }}
              />
            </Form.Group>*/}
            <Form.Group>
              <Form.Label>
                Description
                <p className="small text-secondary mb-0">
                  Recommdended: Min. 100 words (No Limit)
                </p>
              </Form.Label>
              <CKEditor
                id="editor"
                editor={ClassicEditor}
                config={{
                  toolbar: [
                    "heading",
                    "bold",
                    "italic",
                    "link",
                    "numberedList",
                    "bulletedList",
                    "undo",
                    "redo",
                  ],
                }}
                className="mb-3"
                data={eventDetails?.EN?.description ?? ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  saveHTMLDataToStateHandler("description", data, "EN");
                }}
                style={{ height: 400 }}
              />
            </Form.Group>
            {/* <Form.Group className>
              <Form.Label>
                Admin Email
              </Form.Label>
              <Form.Control
                maxLength="100"
                type="text"
                className="mb-3"
                placeholder="Admin Email"
                value={eventDetails?.admin_email}
                name="admin_email"
                onChange={(event) => {
                  setEventDetails((prevDetails) => {
                    return {
                      ...prevDetails,
                      admin_email: event.target.value,
                    };
                  });
                }}
              />
            </Form.Group> */}
            {/* <Form.Group>
              <Form.Label>
                La description
                <p className="small text-secondary mb-0">
                  Recommandé: Min. 100 mots (sans limite)
                </p>
              </Form.Label>
              <CKEditor
                id="editor"
                editor={ClassicEditor}
                config={{
                  toolbar: [
                    "heading",
                    "bold",
                    "italic",
                    "link",
                    "numberedList",
                    "bulletedList",
                    "undo",
                    "redo",
                  ],
                }}
                className="mb-3"
                data={eventDetails?.FR?.description ?? ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  saveHTMLDataToStateHandler("description", data, "FR");
                }}
                style={{ height: 400 }}
              />
            </Form.Group>*/}

            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <br />
              {eventDetails?.start_date && !startDateCalendar ? (
                <a
                  className="border border-secondary rounded p-2 text-white pointer"
                  onClick={() => setStartDateCalendar(true)}
                >
                  {moment(eventDetails?.start_date).format("MMMM DD, YYYY")}
                </a>
              ) : (
                <DatePicker
                  calendarClassName="mb-3 d-block bg-secondary"
                  className="bg-white w-100"
                  isOpen={startDateCalendar}
                  onCalendarOpen={() => setStartDateCalendar(true)}
                  onCalendarClose={() => setStartDateCalendar(false)}
                  format="dd-M-y"
                  placeholder="Select Event Date"
                  value={eventDetails?.start_date}
                  onChange={(date) => {
                    setEventDetails((prevDetails) => {
                      return {
                        ...prevDetails,
                        start_date: date,
                      };
                    });
                  }}
                />
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <br />
              {eventDetails?.end_date && !endDateCalendar ? (
                <a
                  className="border border-secondary rounded p-2 text-white pointer"
                  onClick={() => setEndDateCalendar(true)}
                >
                  {moment(eventDetails?.end_date).format("MMMM DD, YYYY")}
                </a>
              ) : (
                <DatePicker
                  calendarClassName="mb-3 d-block bg-secondary"
                  className="bg-white w-100"
                  isOpen={endDateCalendar}
                  onCalendarOpen={() => setEndDateCalendar(true)}
                  onCalendarClose={() => setEndDateCalendar(false)}
                  format="dd-M-y"
                  placeholder="Select Event Ending Date"
                  value={eventDetails?.end_date}
                  onChange={(date) => {
                    setEventDetails((prevDetails) => {
                      return {
                        ...prevDetails,
                        end_date: date,
                      };
                    });
                  }}
                />
              )}
            </Form.Group>
            <div className="pb-5">
              <Button
                disabled={
                  availableQuantity === 0 || checkingActiveEventPlan || saving
                }
                className="form-control mb-2"
                variant="primary"
                type="submit"
              >
                {saving ? (
                  <span>
                    <Spinner animation="border" size="sm" /> Creating...
                  </span>
                ) : (
                  <span>Next</span>
                )}
              </Button>

              {activeEventPlan?.planType === 0 && availableQuantity === 0 && (
                <p className="mb-0 small">
                  <b>Note: </b>Free limit exhausted, to buy more events{" "}
                  <a
                    className="text-primary pointer"
                    onClick={() => history.push("/pricing")}
                  >
                    click here.
                  </a>
                </p>
              )}

              {activeEventPlan?.planType === 1 && availableQuantity === 0 && (
                <p className="mb-0 small">
                  <b>Note: </b>Limit exhausted, to buy more events{" "}
                  <a
                    className="text-primary pointer"
                    href="mailto:sales@igesia.co"
                    target="_blank"
                  >
                    Contact Us.
                  </a>
                </p>
              )}
            </div>
          </Form>
        </div>
        <div
          className="col-md-6 px-4 px-md-5 py-3 bg-dark d-none"
          // style={{ maxHeight: "85vh", overflow: "auto" }}
        >
          <h5 className="small">PREVIEW</h5>
          <div className="text-dark">
            {eventDetails && eventDetails[currentLanguage]?.event_name && (
              <h4 className="text-s-2-bold text-white">
                {eventDetails[currentLanguage]?.event_name
                  ? eventDetails[currentLanguage]?.event_name
                  : "Event name"}
              </h4>
            )}
            {eventDetails && eventDetails?.featured_image && (
              <img
                src={featured_image_url}
                className="w-100 mb-3 rounded-12"
                alt="..."
              />
            )}

            {eventDetails &&
              eventDetails[currentLanguage]?.small_description && (
                <p className="text-white">
                  {parse(eventDetails[currentLanguage]?.small_description)}
                </p>
              )}

            <div className=" rounded-12 text-white">
              {eventDetails && eventDetails[currentLanguage]?.description && (
                <p className="">
                  {parse(eventDetails[currentLanguage]?.description)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect((state) => ({ currentLanguage: state.currentLanguage }))(
  Addevent
);
